import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { delay, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { SsApiService } from './api/ss-api.service';
import { UserService } from './user/user.service';
import { CommonDataService } from './common-data.service';
import { TimeService } from './time.service';
import { StaticContentService } from './static-content.service';

export const DEP_AMOUNT_STR = 'depAmount';

@Injectable({
  providedIn: 'root',
})
export class LotteryService {
  private _api = inject(SsApiService);
  private _user = inject(UserService);
  private _time = inject(TimeService);
  private _data = inject(CommonDataService);
  private _static = inject(StaticContentService);


  /**
   * Today current time
   * @private
   */
  private _todayTime = this._time.toLocalDate().setHours(0, 0, 0, 0);

  /**
   * Current lottery data by player
   * @private
   */
  private _playerLottery: any = null;

  /**
   * User player lottery tickets
   * @private
   */
  private _usedTickets: number = 0;

  /**
   * Player tickets amount
   * @private
   */
  private _playerTicketsAmount: number = 0;

  /**
   * Plaer ticket status
   */
  public updateLotteryStatus$: ReplaySubject<any> = new ReplaySubject(1);

  /**
   * Player lotteries list
   */
  public playerLotteryList$: ReplaySubject<any> = this._loadPlayerLotteryList$();

  public isLotteryAvailableForStag$ = this._static.item({ slug: 'lottery-stag' }).pipe(
    map((e) => {
      this._isLotteryAvaliableForStag = !!e?.length;
      return !!e?.length;
    }),
    shareReplay(),
  );

  private _isLotteryAvaliableForStag = false;

  public get isLotteryAvaliableForStag() {
    return this._isLotteryAvaliableForStag;
  }

  /**
   * Load player lotteries
   * @private
   */
  private _loadPlayerLotteryList$() {
    return this._api.getLotteriesPlayer().pipe(
      tap(lotteryList => {
        if (lotteryList.length) {
          this._playerLottery = lotteryList[0];
        }
      }),
    ) as ReplaySubject<any>;
  }

  /**
   * Check current lottery by today time
   * @param lotteryList
   * @private
   */
  private _isCheckCurrentLottery$(lotteryList: any) {
    if (lotteryList?.length) {
      return of(lotteryList[0]);
    } else {
      return of(null);
    }
  }

  /**
   * Get current lottery by today date
   */
  public getCurrentLottery$(): Observable<any> {
    return this._api.getLotteries().pipe(
      delay(100),
      switchMap((lotteryList: any) => this._isCheckCurrentLottery$(lotteryList)),
      map(lottery => {
        return {
          ...lottery,
          end_at: this._time.toLocalDate(lottery?.end_at),
        };
      }),
      shareReplay(1),
    );
  }

  /**
   * Get previous user lottery by lotteries list length
   */
  public getPrevLottery$(id: number): Observable<any> {
    return this._api.getLotteryInfoById(id - 1);
  }

  /**
   * Set amount tickets by deposit amount value
   * @param amount
   */
  public setDepositAmount(amount: number | string) {
    if (!this._playerLottery) {
      return;
    }
    ;

    if (typeof amount === 'string') {
      amount = Number(amount.replace(/,/g, ''));
    }

    const currencyName = this._user.info.currency;
    const lotteryCurrencyInfo = this.getLotteryCurrencyInfo(currencyName);
    const step = this._data.subunitsToUnits(lotteryCurrencyInfo?.length && lotteryCurrencyInfo[0]?.price_cents, currencyName);
    this._playerTicketsAmount = Math.min(this._playerLottery.max_tickets_count_to_player - this.usedTickets, Math.floor(amount / step));
  }

  public getLotteryCurrencyInfo(currency) {
    return this._playerLottery?.deposit_scale_rules[currency];
  }

  /**
   * Access to used tickets
   */
  public get usedTickets() {
    return this._usedTickets;
  }

  /**
   * Access to player tickets
   */
  public get playerTickets() {
    return this._playerTicketsAmount;
  }


  public get lotteryAvailableTickets() {
    return this._playerLottery?.max_tickets_count_to_player;
  }

  /**
   * Set used tickets
   * @param value
   */
  public set usedTickets(value: number) {
    this._usedTickets = value;
  }

  /**
   * Activate lottery
   */
  public selectLottery() {
    this._api.toggleSelectLottery(this._user.info.currency, {
      currency: this._user.info.currency, 'selected_lottery': this._playerLottery['id'],
    }).subscribe(() => {
      this._user.info.lottery = this._playerLottery['id'];
      this.updateLotteryStatus$.next(this._playerLottery['id']);
    });
  }

  /**
   * Deactivate lottery
   */
  public unSelectLottery() {
    this._api.toggleSelectLottery(this._user.info.currency, {
      currency: this._user.info.currency, 'selected_lottery': null,
    }).subscribe(() => {
      this._user.info.lottery = null;
      this.updateLotteryStatus$.next(null);
    });
  }

  /**
   * Check lottery count tickets
   */
  public checkLotteryCountTickets$(): Observable<any> {
    return this.playerLotteryList$.pipe(
      switchMap((currentLottery: any) => currentLottery.length ? this._api.getLotteriesIdStatus(currentLottery[0]?.id) : EMPTY),
      tap(lottery => {
        this.usedTickets = lottery?.tickets?.length;
        this.selectLottery();
      }),
    );
  }

  /**
   * Check side menu by active lottery
   */
  public checkLotteryLinkVisibility$(): Observable<any> {
    return this._api.getLotteries().pipe(
      switchMap((lotteryList: any) => this._isCheckCurrentLottery$(lotteryList)),
    );
  }

}
